import { useNavigate, useSearch } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'motion/react'
import { useMemo } from 'react'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'
import { useTeaCategories, useTeawareCategories } from '@/entities/product/api/use-categories'
import type { CatalogSection } from '@/widgets/catalog-section-switcher'

/**
 * Виджет: Быстрые категории (чипы) для каталога
 *
 * Горизонтальный скролл категорий с активным состоянием.
 * При клике обновляет query параметр category.
 * Показывает подкатегории выбранной категории.
 * Использует категории с сервера.
 *
 * @param section - Секция каталога ('tea' | 'teaware' | 'product_set')
 */
export interface CatalogCategoriesChipsProps {
  section: CatalogSection
}

export function CatalogCategoriesChips({ section }: CatalogCategoriesChipsProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()

  // Наборы не категоризуются — чипы категорий не показываем
  if (section === 'product_set') return null
  
  // Получаем категории с сервера в зависимости от секции
  const teaCategoriesQuery = useTeaCategories()
  const teawareCategoriesQuery = useTeawareCategories()
  
  const categoriesQuery = section === 'tea' ? teaCategoriesQuery : teawareCategoriesQuery
  const rawCategories = categoriesQuery.data?.data || []
  const isLoading = categoriesQuery.isLoading
  
  // Сортируем категории по sortOrder (sort_order с сервера), затем по названию
  const categories = useMemo(() => 
    [...rawCategories].sort((a, b) => {
      const orderA = a.sortOrder ?? 999
      const orderB = b.sortOrder ?? 999
      if (orderA !== orderB) {
        return orderA - orderB
      }
      return a.name.localeCompare(b.name, 'ru')
    }),
    [rawCategories]
  )
  
  // Получаем search из маршрута (если доступен) или из URL
  let activeCategory: string | undefined
  let activeSubcategory: string | undefined
  try {
    const search = useSearch({ strict: false })
    activeCategory = (search as any)?.category
    activeSubcategory = (search as any)?.subcategory
  } catch {
    // Если маршрут не найден, используем URL
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      activeCategory = searchParams.get('category') || undefined
      activeSubcategory = searchParams.get('subcategory') || undefined
    }
  }
  
  // Находим выбранную категорию и её подкатегории
  const selectedCategory = useMemo(() => 
    categories.find(cat => cat.id === activeCategory),
    [categories, activeCategory]
  )
  
  // Сортируем подкатегории
  const subcategories = useMemo(() => {
    if (!selectedCategory?.subcategories) return []
    return [...selectedCategory.subcategories].sort((a, b) => {
      const orderA = a.sortOrder ?? a.orderIndex ?? 999
      const orderB = b.sortOrder ?? b.orderIndex ?? 999
      if (orderA !== orderB) {
        return orderA - orderB
      }
      return a.name.localeCompare(b.name, 'ru')
    })
  }, [selectedCategory])
  
  const handleCategoryClick = (categoryId: string) => {
    hapticFeedback.impactOccurred('light')
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        category: categoryId,
        subcategory: undefined, // Сбрасываем подкатегорию при смене категории
      }),
    } as any)
  }
  
  const handleSubcategoryClick = (subcategoryId: string) => {
    hapticFeedback.selectionChanged()
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        subcategory: activeSubcategory === subcategoryId ? undefined : subcategoryId,
      }),
    } as any)
  }
  
  const handleClearCategory = () => {
    hapticFeedback.impactOccurred('light')
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        category: undefined,
        subcategory: undefined,
      }),
    } as any)
  }
  
  // Skeleton loader для категорий
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex gap-3 overflow-x-auto lg:flex-wrap pb-2 lg:pb-0 scrollbar-hide">
          <div
            className="flex-shrink-0 px-5 py-2.5 rounded-[22px] bg-gray-200 dark:bg-dark-elevated animate-pulse"
            style={{ width: '70px', height: '40px' }}
          />
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex-shrink-0 px-5 py-2.5 rounded-[22px] bg-gray-200 dark:bg-dark-elevated animate-pulse"
              style={{ width: '100px', height: '40px' }}
            />
          ))}
        </div>
      </div>
    )
  }
  
  // Если категорий нет, не показываем компонент
  if (categories.length === 0) {
    return null
  }
  
  // Определяем, активен ли чип "Все" (когда нет выбранной категории)
  const isAllActive = !activeCategory
  
  return (
    <div className="space-y-3">
      {/* Основные категории */}
      <div className="flex gap-3 overflow-x-auto lg:flex-wrap pb-2 lg:pb-0 scrollbar-hide">
        {/* Чип "Все" в начале списка */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          whileHover={{ scale: isAllActive ? 1 : 1.03 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
          onClick={handleClearCategory}
          className={cn(
            'flex-shrink-0 px-5 py-2.5 rounded-[22px]',
            'transition-all duration-200 touch-target font-medium text-sm',
            'whitespace-nowrap',
            {
              'bg-primary dark:bg-primary-dark border-2 border-primary dark:border-primary-dark text-white shadow-md dark:shadow-lg scale-[1.03]': isAllActive,
              'bg-white dark:bg-dark-elevated border border-gray-300 dark:border-gray-600 text-ink dark:text-white shadow-sm dark:shadow-md hover:bg-gray-50 dark:hover:bg-dark-card hover:border-primary/40 dark:hover:border-primary-dark/40': !isAllActive,
            }
          )}
        >
          Все
        </motion.button>
        
        {categories.map((category) => {
          const isActive = activeCategory === category.id
          
          return (
            <motion.button
              key={category.id}
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: isActive ? 1 : 1.03 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              onClick={() => handleCategoryClick(category.id)}
              className={cn(
                'flex-shrink-0 px-5 py-2.5 rounded-[22px]',
                'transition-all duration-200 touch-target font-medium text-sm',
                'whitespace-nowrap',
                {
                  'bg-primary dark:bg-primary-dark border-2 border-primary dark:border-primary-dark text-white shadow-md dark:shadow-lg scale-[1.03]': isActive,
                  'bg-white dark:bg-dark-elevated border border-gray-300 dark:border-gray-600 text-ink dark:text-white shadow-sm dark:shadow-md hover:bg-gray-50 dark:hover:bg-dark-card hover:border-primary/40 dark:hover:border-primary-dark/40': !isActive,
                }
              )}
            >
              {category.name}
            </motion.button>
          )
        })}
      </div>
      
      {/* Подкатегории выбранной категории */}
      <AnimatePresence mode="wait">
        {subcategories.length > 0 && (
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ 
              duration: 0.25, 
              ease: [0.4, 0, 0.2, 1],
              opacity: { duration: 0.2 }
            }}
            className="overflow-hidden"
          >
            <div className="flex gap-2 overflow-x-auto lg:flex-wrap pb-2 lg:pb-0 scrollbar-hide">
              {/* Чип "Все подкатегории" */}
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  hapticFeedback.selectionChanged()
                  navigate({
                    search: (prev: Record<string, unknown>) => ({
                      ...prev,
                      subcategory: undefined,
                    }),
                  } as any)
                }}
                className={cn(
                  'flex-shrink-0 px-4 py-2 rounded-[18px]',
                  'transition-all duration-200 touch-target font-medium text-xs',
                  'whitespace-nowrap',
                  {
                    'bg-primary/15 dark:bg-primary-dark/25 border border-primary/30 dark:border-primary-dark/40 text-primary dark:text-primary-light': !activeSubcategory,
                    'bg-gray-100 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-400 hover:bg-gray-150 dark:hover:bg-dark-elevated': activeSubcategory,
                  }
                )}
              >
                Все {selectedCategory?.name?.toLowerCase()}
              </motion.button>
              
              {subcategories.map((subcategory, index) => {
                const isSubActive = activeSubcategory === subcategory.id
                
                return (
                  <motion.button
                    key={subcategory.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.05 + index * 0.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleSubcategoryClick(subcategory.id)}
                    className={cn(
                      'flex-shrink-0 px-4 py-2 rounded-[18px]',
                      'transition-all duration-200 touch-target font-medium text-xs',
                      'whitespace-nowrap',
                      {
                        'bg-primary/15 dark:bg-primary-dark/25 border border-primary/30 dark:border-primary-dark/40 text-primary dark:text-primary-light': isSubActive,
                        'bg-gray-100 dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-400 hover:bg-gray-150 dark:hover:bg-dark-elevated': !isSubActive,
                      }
                    )}
                  >
                    {subcategory.name}
                  </motion.button>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

